if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      #controller = null;

      connectedCallback() {
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[aria-live="polite"]'),
          overlay: this.querySelector('.country-selector__overlay'),
        };

        if (!this.elements.input || !this.elements.button || !this.elements.panel) return;

        this.header = document.querySelector('sticky-header');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.#controller = new AbortController();
        const { signal } = this.#controller;

        this.elements.button.addEventListener('click', () => this.toggleSelector(), { signal });
        this.addEventListener('keydown', (event) => this.onContainerKeyDown(event), { signal });
        this.addEventListener('focusout', (event) => this.onFocusOut(event), { signal });

        for (const item of this.querySelectorAll('[data-value]')) {
          item.addEventListener('click', (event) => this.onItemClick(event), { signal });
        }

        if (this.elements.search) {
          this.elements.search.addEventListener('input', () => this.filterCountries(), { signal });
          this.elements.search.addEventListener('focus', () => this.onSearchFocus(), { signal });
          this.elements.search.addEventListener('blur', () => this.onSearchBlur(), { signal });
          this.elements.search.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') event.preventDefault();
          }, { signal });
        }

        this.elements.closeButton?.addEventListener('click', () => this.hidePanel(true), { signal });
        this.elements.overlay?.addEventListener('click', () => this.hidePanel(true), { signal });
        this.elements.resetButton?.addEventListener('click', (event) => this.resetFilter(event), { signal });
        this.elements.resetButton?.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      }

      disconnectedCallback() {
        this.#controller?.abort();
      }

      get isOpen() {
        return this.elements?.button?.getAttribute('aria-expanded') === 'true';
      }

      get visibleItems() {
        return Array.from(this.querySelectorAll('[data-value]')).filter(
          (item) => !item.parentElement.hidden && !item.closest('.popular-countries.hidden')
        );
      }

      toggleSelector() {
        if (this.isOpen) {
          this.hidePanel(false);
          return;
        }
        this.openSelector();
      }

      openSelector() {
        this.elements.panel.hidden = false;
        this.elements.button.setAttribute('aria-expanded', 'true');
        if (this.hasAttribute('data-prevent-hide') && this.header) this.header.preventHide = true;
        if (this.elements.search && this.mql.matches) this.elements.search.focus();
      }

      hidePanel(returnFocus = false) {
        if (!this.elements?.button || !this.elements.panel) return;

        this.elements.panel.hidden = true;
        this.elements.button.setAttribute('aria-expanded', 'false');
        if (this.header) this.header.preventHide = false;

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.removeAttribute('aria-activedescendant');
        }

        if (returnFocus) this.elements.button.focus();
      }

      onFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.hidePanel(false);
      }

      onContainerKeyDown(event) {
        if (event.key === 'Escape' && this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.hidePanel(true);
          return;
        }

        if (event.target === this.elements.button && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          event.preventDefault();
          if (!this.isOpen) this.openSelector();
          const items = this.visibleItems;
          const item = event.key === 'ArrowDown' ? items[0] : items[items.length - 1];
          item?.focus();
          return;
        }

        const items = this.visibleItems;
        const currentIndex = items.indexOf(document.activeElement);
        if (currentIndex < 0) return;

        let nextIndex = currentIndex;
        let handled = true;
        if (event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % items.length;
        } else if (event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + items.length) % items.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = items.length - 1;
        } else {
          handled = false;
        }
        if (!handled) return;

        event.preventDefault();
        items[nextIndex]?.focus();
        this.elements.search?.setAttribute('aria-activedescendant', items[nextIndex].id);
      }

      onItemClick(event) {
        event.preventDefault();
        this.elements.input.value = event.currentTarget.dataset.value;
        this.querySelector('form')?.submit();
      }

      normalizeString(value) {
        return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
      }

      filterCountries() {
        if (!this.elements.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        let visibleCountries = 0;

        this.elements.resetButton?.classList.toggle('hidden', searchValue.length === 0);
        popularCountries?.classList.toggle('hidden', searchValue.length > 0);

        for (const item of this.querySelectorAll('[data-value]')) {
          const country = item.querySelector('.country');
          if (!country) continue;
          const matches = this.normalizeString(country.textContent).includes(searchValue);
          item.parentElement.hidden = !matches;
          if (matches) visibleCountries += 1;
        }

        if (this.elements.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) {
          this.elements.liveRegion.textContent = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        const countrySelector = this.querySelector('.country-selector');
        const countryList = this.querySelector('.country-selector__list');
        if (countrySelector) countrySelector.scrollTop = 0;
        if (countryList) countryList.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        if (!this.elements.search) return;
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon?.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search?.value) {
          this.elements.searchIcon?.classList.remove('country-filter__search-icon--hidden');
        }
      }
    }
  );
}
